import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { RootState } from '..';

interface ModulesState {
  performanceManagementQuantitativeEnabled: boolean;
  performanceManagementCascadeEnabled: boolean;
  employeeCompetencyEnabled: boolean;
  manualKpiWeightsAdjustmentEnabled: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ModulesState = {
  performanceManagementQuantitativeEnabled: false,
  performanceManagementCascadeEnabled: false,
  employeeCompetencyEnabled: false,
  manualKpiWeightsAdjustmentEnabled: false,
  status: 'idle',
  error: null,
};

export const fetchPerformanceManagementQuantitativeEnabled = createAsyncThunk(
  'modules/fetchPerformanceManagementQuantitativeEnabled',
  async () => {
    const response = await api.get('/module/PerformanceManagementQuantitative/is-enabled');
    return response.data.data.isEnabled;
  }
);

export const fetchPerformanceManagementCascadeEnabled = createAsyncThunk(
  'modules/fetchPerformanceManagementCascadeEnabled',
  async () => {
    const response = await api.get('/module/PerformanceManagementCascade/is-enabled');
    return response.data.data.isEnabled;
  }
);

export const fetchEmployeeCompetencyEnabled = createAsyncThunk(
  'modules/fetchEmployeeCompetencyEnabled',
  async () => {
    const response = await api.get('/module/EmployeeCompetency/is-enabled');
    return response.data.data.isEnabled;
  }
);

// The module document is created lazily the first time an admin opens the Modules screen, so
// a tenant asking before that gets a 404. That is "not enabled", not an error worth surfacing.
export const fetchManualKpiWeightsAdjustmentEnabled = createAsyncThunk(
  'modules/fetchManualKpiWeightsAdjustmentEnabled',
  async () => {
    try {
      const response = await api.get('/module/PerformanceManagementManualKpiWeights/is-enabled');
      return response.data?.data?.isEnabled ?? response.data?.isEnabled ?? false;
    } catch {
      return false;
    }
  }
);

const modulesSlice = createSlice({
  name: 'modules',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPerformanceManagementQuantitativeEnabled.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPerformanceManagementQuantitativeEnabled.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.performanceManagementQuantitativeEnabled = action.payload;
      })
      .addCase(fetchPerformanceManagementQuantitativeEnabled.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch module status';
      })
      .addCase(fetchPerformanceManagementCascadeEnabled.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPerformanceManagementCascadeEnabled.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.performanceManagementCascadeEnabled = action.payload;
      })
      .addCase(fetchPerformanceManagementCascadeEnabled.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch module status';
      })
      .addCase(fetchEmployeeCompetencyEnabled.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEmployeeCompetencyEnabled.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employeeCompetencyEnabled = action.payload;
      })
      .addCase(fetchEmployeeCompetencyEnabled.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch module status';
      })
      .addCase(fetchManualKpiWeightsAdjustmentEnabled.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchManualKpiWeightsAdjustmentEnabled.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.manualKpiWeightsAdjustmentEnabled = action.payload;
      })
      .addCase(fetchManualKpiWeightsAdjustmentEnabled.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch module status';
      });
  },
});

export const selectPerformanceManagementQuantitativeEnabled = (state: RootState) => state.modules.performanceManagementQuantitativeEnabled;
export const selectPerformanceManagementCascadeEnabled = (state: RootState) => state.modules.performanceManagementCascadeEnabled;
export const selectEmployeeCompetencyEnabled = (state: RootState) => state.modules.employeeCompetencyEnabled;
export const selectManualKpiWeightsAdjustmentEnabled = (state: RootState) => state.modules.manualKpiWeightsAdjustmentEnabled;
export default modulesSlice.reducer; 